import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Pool, KnockoutEntryPicks, KnockoutTeamPick, NCAATeam, PoolTeam, KNOCKOUT_ROUND_REQUIREMENTS } from '../types/pool';
import { api } from '../services/api';
import FeeNotice from '../components/FeeNotice';

// Region colors for visual distinction
const REGION_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  East: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700' },
  West: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700' },
  South: { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-700' },
  Midwest: { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-700' },
};

// Represents a game day derived from pool events
interface GameDay {
  date: string;       // YYYY-MM-DD
  label: string;      // e.g. "Round of 64 Day 1"
  round: string;      // event.round or event.name
  picksRequired: number;
  buyBackPicksRequired?: number; // extra picks needed if this is a buy-back day
  bracketDay: number | null; // 1 or 2 for early rounds, null for Final Four/Championship (show all teams)
  scheduledAt: string; // ISO datetime — deadline for this day's picks (when games tip off)
}

// Format date for display
function formatDateDisplay(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

// Derive bracketDay (1 or 2) from an event name.
// "Day 1" → 1, "Day 2" → 2, Final Four / Championship → null (all teams eligible)
function deriveBracketDay(eventName: string): number | null {
  if (/day\s*1/i.test(eventName)) return 1;
  if (/day\s*2/i.test(eventName)) return 2;
  // Final Four and Championship have no day split — all remaining teams are eligible
  return null;
}

// Look up knockout round requirements with flexible matching.
// Tries exact match first, then tries stripping "Day 1"/"Day 2" suffix.
function lookupRoundRequirements(roundKey: string): { picksRequired: number; buyBackAvailable?: boolean; buyBackPicksRequired?: number } {
  // Exact match first
  if (KNOCKOUT_ROUND_REQUIREMENTS[roundKey]) {
    return KNOCKOUT_ROUND_REQUIREMENTS[roundKey];
  }

  // Try stripping " Day 1" / " Day 2" suffix for base round name
  const baseRound = roundKey.replace(/\s*Day\s*\d+$/i, '').trim();
  if (baseRound !== roundKey && KNOCKOUT_ROUND_REQUIREMENTS[baseRound]) {
    return KNOCKOUT_ROUND_REQUIREMENTS[baseRound];
  }

  // Try appending Day suffix if event name doesn't have one but requirements do
  const bracketDay = deriveBracketDay(roundKey);
  if (!bracketDay) {
    // No day in name — try matching with " Day 1" suffix (for R64/R32 style)
    const withDay1 = `${roundKey} Day 1`;
    if (KNOCKOUT_ROUND_REQUIREMENTS[withDay1]) {
      return KNOCKOUT_ROUND_REQUIREMENTS[withDay1];
    }
  }

  return { picksRequired: 1 };
}

// Derive game-day schedule from pool events, mapped to knockout requirements
function buildGameDays(events: any[]): GameDay[] {
  if (!events || events.length === 0) return [];

  return events
    .map((evt: any) => {
      const date = new Date(evt.scheduledAt).toISOString().split('T')[0];
      const roundKey = evt.name || evt.round || '';

      // Look up pick requirements with flexible matching
      // Try event name first, fall back to round field
      const req = roundKey
        ? lookupRoundRequirements(roundKey)
        : lookupRoundRequirements(evt.round || '');

      return {
        date,
        label: roundKey,
        round: evt.round || roundKey,
        picksRequired: req.picksRequired,
        buyBackPicksRequired: req.buyBackPicksRequired,
        bracketDay: deriveBracketDay(roundKey),
        scheduledAt: evt.scheduledAt, // deadline for this day's picks
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
}

export function KnockoutEntryForm() {
  const { poolId, entryId } = useParams<{ poolId: string; entryId?: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  // State
  const [pool, setPool] = useState<Pool | null>(null);
  const [teams, setTeams] = useState<NCAATeam[]>([]);
  const [gameDays, setGameDays] = useState<GameDay[]>([]);
  const [eliminatedTeamIds, setEliminatedTeamIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Entry state
  const [entryName, setEntryName] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [dailyPicks, setDailyPicks] = useState<Record<string, KnockoutTeamPick[]>>({});
  const [usedTeams, setUsedTeams] = useState<string[]>([]);
  const [entryStatus, setEntryStatus] = useState<'ALIVE' | 'ELIMINATED' | 'BOUGHT_BACK'>('ALIVE');
  const [buyBackCount, setBuyBackCount] = useState(0);

  // Existing entry (for editing)
  const [existingEntry, setExistingEntry] = useState<any>(null);

  // Filter state
  const [selectedRegion, setSelectedRegion] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: window.location.pathname },
      });
      return;
    }
    loadData();
  }, [poolId, isAuthenticated]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load pool data
      const poolData = await api.getPool(poolId!);
      setPool(poolData);

      // Check if pool is NCAA Basketball Knockout
      if (poolData.type !== 'NCAA_BASKETBALL_KNOCKOUT') {
        setError('This form is only for NCAA Basketball Knockout pools');
        return;
      }

      // Load teams for this pool - map PoolTeam to NCAATeam format
      const poolTeams: PoolTeam[] = poolData.teams || [];
      const ncaaTeams: NCAATeam[] = poolTeams.map(pt => ({
        id: pt.team.id,
        name: pt.team.name,
        seed: pt.seed || 16,
        region: (pt.metadata?.region as NCAATeam['region']) || 'East',
        conference: pt.team.conference,
        bracketDay: pt.metadata?.bracketDay as number | undefined,
      }));
      setTeams(ncaaTeams);

      // Load events/schedule for this pool
      try {
        const eventsData = await api.getPoolEventsPublic(poolId!);
        const events = eventsData.events || [];
        const days = buildGameDays(events);
        setGameDays(days);

        // Default to the first upcoming game day (or first day if all are past)
        const today = new Date().toISOString().split('T')[0];
        const upcomingDay = days.find(d => d.date >= today);
        if (upcomingDay) {
          setSelectedDate(upcomingDay.date);
        } else if (days.length > 0) {
          setSelectedDate(days[0].date);
        }
      } catch {
        // No events loaded — schedule not imported yet
      }

      // Load eliminated teams from the dedicated cross-pool endpoint
      try {
        const eliminatedData = await api.getEliminatedNcaaTeams();
        setEliminatedTeamIds(new Set(eliminatedData.eliminatedTeamIds || []));
      } catch {
        // Couldn't determine eliminated teams — show all as available
      }

      // Only load existing entry when editing (URL has entryId)
      if (entryId) {
        const token = await getAccessTokenSilently();
        try {
          const myEntries = await api.getMyEntries(poolId!, token);
          const myEntry = myEntries.find(e => e.id === entryId);
          if (myEntry) {
            setExistingEntry(myEntry);
            setEntryName(myEntry.entryName || '');

            // Parse existing picks
            if (myEntry.picks && (myEntry.picks as any).type === 'DAILY_PICKS') {
              const picks = myEntry.picks as unknown as KnockoutEntryPicks;
              setDailyPicks(picks.picks || {});
              setUsedTeams(picks.usedTeams || []);
              setEntryStatus(picks.status || 'ALIVE');
              setBuyBackCount(picks.buyBackCount || 0);
            }
          }
        } catch {
          // No existing entry - that's fine
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pool data');
    } finally {
      setLoading(false);
    }
  };

  // Look up the current game day from schedule
  const currentGameDay = useMemo(
    () => gameDays.find(d => d.date === selectedDate),
    [gameDays, selectedDate]
  );

  // Get required picks for the selected date
  // Bought-back entries need extra picks on their first day back
  const getRequiredPicks = (date: string): number => {
    const day = gameDays.find(d => d.date === date);
    const baseRequired = day?.picksRequired ?? 1;

    // If entry was just bought back, check if this day has buyback pick requirements
    // and the user hasn't already submitted picks for this day
    if (entryStatus === 'BOUGHT_BACK') {
      const roundReq = day ? lookupRoundRequirements(day.label) : null;
      if (roundReq?.buyBackPicksRequired) {
        return roundReq.buyBackPicksRequired;
      }
    }

    return baseRequired;
  };

  // Check if a team is available (not used yet)
  const isTeamAvailable = (teamId: string): boolean => {
    return !usedTeams.includes(teamId);
  };

  // Check if a team is selected for the current date
  const isTeamSelectedForDate = (teamId: string, date: string): boolean => {
    const picks = dailyPicks[date] || [];
    return picks.some(p => p.teamId === teamId);
  };

  // Toggle team selection for a date
  const toggleTeamSelection = (team: NCAATeam, date: string) => {
    const currentPicks = dailyPicks[date] || [];
    const isSelected = currentPicks.some(p => p.teamId === team.id);
    const requiredPicks = getRequiredPicks(date);

    // Clear any previous error when user interacts
    setError(null);

    if (isSelected) {
      // Remove team
      const newPicks = currentPicks.filter(p => p.teamId !== team.id);
      setDailyPicks({ ...dailyPicks, [date]: newPicks });

      // Remove from used teams
      setUsedTeams(usedTeams.filter(id => id !== team.id));
    } else {
      // Check if we can add more picks
      if (currentPicks.length >= requiredPicks) {
        setError(`You can only select ${requiredPicks} team(s) for this day`);
        return;
      }

      // Check if team is available
      if (!isTeamAvailable(team.id)) {
        setError(`${team.name} has already been used in a previous day`);
        return;
      }

      // Add team
      const newPick: KnockoutTeamPick = {
        teamId: team.id,
        teamName: team.name,
        seed: team.seed,
        region: team.region,
        result: 'PENDING',
      };
      setDailyPicks({ ...dailyPicks, [date]: [...currentPicks, newPick] });

      // Add to used teams
      setUsedTeams([...usedTeams, team.id]);
    }
  };

  // Filter teams based on bracketDay, region, and search
  const filteredTeams = teams.filter(team => {
    // Only show teams that play on the selected game day
    // If the game day has a bracketDay (1 or 2), filter to matching teams.
    // If bracketDay is null (Final Four / Championship), show all teams.
    if (currentGameDay?.bracketDay != null && team.bracketDay != null) {
      if (team.bracketDay !== currentGameDay.bracketDay) return false;
    }
    if (selectedRegion !== 'all' && team.region !== selectedRegion) return false;
    if (searchQuery && !team.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  // Group teams by region
  const teamsByRegion = filteredTeams.reduce((acc, team) => {
    const region = team.region || 'Unknown';
    if (!acc[region]) acc[region] = [];
    acc[region].push(team);
    return acc;
  }, {} as Record<string, NCAATeam[]>);

  // Sort teams within each region by seed
  Object.keys(teamsByRegion).forEach(region => {
    teamsByRegion[region].sort((a, b) => a.seed - b.seed);
  });

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Re-check deadline at submit time
    // For knockout EDITS (adding daily picks), allow even after deadline
    if (pool && new Date(pool.deadline) < new Date() && !existingEntry) {
      setError('The deadline for this pool has passed. You can no longer submit new entries.');
      return;
    }

    // Must have game days loaded
    if (gameDays.length === 0 || !selectedDate) {
      setError('No game days available. The schedule must be imported before you can submit picks.');
      return;
    }

    // Must have at least one date with picks
    const allPickDates = Object.keys(dailyPicks).filter(d => (dailyPicks[d] || []).length > 0);
    if (allPickDates.length === 0) {
      setError('Please select at least one team before submitting.');
      return;
    }

    // Validate picks for selected date
    const currentPicks = dailyPicks[selectedDate] || [];
    const requiredPicks = getRequiredPicks(selectedDate);

    if (currentPicks.length !== requiredPicks) {
      setError(`Please select exactly ${requiredPicks} team(s) for ${formatDateDisplay(selectedDate)}`);
      return;
    }

    const hasEntryFee = pool?.entryFee && Number(pool.entryFee) > 0;

    try {
      setSubmitting(true);
      const token = await getAccessTokenSilently();

      const picks: KnockoutEntryPicks = {
        type: 'DAILY_PICKS',
        status: entryStatus,
        buyBackCount,
        eliminatedOn: null,
        usedTeams,
        picks: dailyPicks,
      };

      const entryData: any = { entryName, picks };

      if (existingEntry) {
        // Update existing entry
        await api.updateEntry(poolId!, existingEntry.id, entryData, token);
        navigate(`/pools/${poolId}`);
      } else {
        // Create new entry
        await api.createEntry(poolId!, entryData, token);
        if (hasEntryFee) {
          // Payment v2: entry is saved — take them to Settle Up to pay
          navigate('/settle-up', {
            state: { justSubmitted: true, submittedPool: pool?.name },
          });
        } else {
          navigate(`/pools/${poolId}`);
        }
      }
    } catch (err: any) {
      console.error('Failed to submit entry:', err);
      let errorMessage: string;
      if (err?.message?.includes('login_required') || err?.message?.includes('Missing Refresh Token') || err?.error === 'login_required' || err?.message?.includes('Login required')) {
        errorMessage = 'Your session has expired. Please log in again to submit your entry.';
        setTimeout(() => {
          loginWithRedirect({ appState: { returnTo: window.location.pathname } });
        }, 2000);
      } else {
        errorMessage = err instanceof Error ? err.message : 'Failed to save picks';
      }
      setError(errorMessage);
      // Scroll error into view
      setTimeout(() => {
        document.querySelector('[role="alert"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pool...</p>
        </div>
      </div>
    );
  }

  if (!pool) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md">
          <div className="text-red-600 text-center">
            <h3 className="text-lg font-semibold mb-2">Error</h3>
            <p className="text-gray-600 mb-4">{error || 'Pool not found'}</p>
            <Link
              to="/pools"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Back to Pools
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const currentPicks = dailyPicks[selectedDate] || [];
  const requiredPicks = getRequiredPicks(selectedDate);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to={`/pools/${poolId}`}
            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Pool
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{pool.name}</h1>
          <p className="text-gray-600">Make your picks for the NCAA Tournament Knockout Pool</p>
        </div>

        {/* Entry Status Banner */}
        {existingEntry && (
          <div className={`mb-6 p-4 rounded-lg border-2 ${
            entryStatus === 'ALIVE'
              ? 'bg-green-50 border-green-300'
              : entryStatus === 'ELIMINATED'
              ? 'bg-red-50 border-red-300'
              : 'bg-yellow-50 border-yellow-300'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`text-2xl`}>
                  {entryStatus === 'ALIVE' ? '✅' : entryStatus === 'ELIMINATED' ? '❌' : '🔄'}
                </span>
                <div>
                  <p className="font-semibold text-gray-900">
                    Status: {entryStatus}
                  </p>
                  <p className="text-sm text-gray-600">
                    Buy-backs used: {buyBackCount}/3
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Teams used: {usedTeams.length}/68</p>
                <p className="text-sm text-gray-600">Teams remaining: {68 - usedTeams.length}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content - Team Selection */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit}>
              {/* Entry Name */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entry Name (Optional)
                </label>
                <input
                  type="text"
                  value={entryName}
                  onChange={(e) => setEntryName(e.target.value)}
                  placeholder="My March Madness Entry"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  maxLength={50}
                />
              </div>

              {/* Game Day Selection */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Game Day</h2>
                {gameDays.length === 0 ? (
                  <p className="text-gray-500 text-sm">No game days scheduled yet. The schedule will be available once the admin imports it.</p>
                ) : (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                      {gameDays.map((day) => {
                        const dayPicks = dailyPicks[day.date] || [];
                        const isSelected = selectedDate === day.date;
                        const isComplete = dayPicks.length === day.picksRequired;
                        const hasPartialPicks = dayPicks.length > 0 && !isComplete;
                        const isDayLocked = new Date() >= new Date(day.scheduledAt);

                        return (
                          <button
                            key={day.date}
                            type="button"
                            onClick={() => setSelectedDate(day.date)}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                : isDayLocked && isComplete
                                ? 'border-gray-300 bg-gray-50 opacity-70'
                                : isComplete
                                ? 'border-green-300 bg-green-50'
                                : hasPartialPicks
                                ? 'border-yellow-300 bg-yellow-50'
                                : 'border-gray-200 bg-white hover:border-gray-400'
                            }`}
                          >
                            <p className="text-xs font-medium text-gray-500">
                              {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </p>
                            <p className="text-sm font-semibold text-gray-900 truncate">{day.label}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {dayPicks.length}/{day.picksRequired} pick{day.picksRequired > 1 ? 's' : ''}
                              {isComplete && !isDayLocked && ' ✓'}
                              {isDayLocked && ' 🔒'}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                    {currentGameDay && (() => {
                      const dayLocked = new Date() >= new Date(currentGameDay.scheduledAt);
                      const deadline = new Date(currentGameDay.scheduledAt);
                      return (
                        <div className="mt-3">
                          <p className="text-sm text-gray-600">
                            {formatDateDisplay(selectedDate)} — Pick {requiredPicks} winner{requiredPicks > 1 ? 's' : ''}
                          </p>
                          {dayLocked ? (
                            <p className="text-xs text-red-600 font-medium mt-1">
                              🔒 Picks locked — games started at {deadline.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            </p>
                          ) : (
                            <p className="text-xs text-green-600 font-medium mt-1">
                              Deadline: {deadline.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at {deadline.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>

              {/* Filters */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Filter by Region
                    </label>
                    <select
                      value={selectedRegion}
                      onChange={(e) => setSelectedRegion(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Regions</option>
                      <option value="East">East</option>
                      <option value="West">West</option>
                      <option value="South">South</option>
                      <option value="Midwest">Midwest</option>
                    </select>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search Teams
                    </label>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by team name..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Team Selection by Region */}
              {(() => {
                const selectedDayLocked = currentGameDay ? new Date() >= new Date(currentGameDay.scheduledAt) : false;
                return (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                {selectedDayLocked && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm font-medium">
                    🔒 This day's picks are locked — games have already started. You can view your picks but not change them.
                  </div>
                )}
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Select Teams ({currentPicks.length}/{requiredPicks})
                </h2>

                {teams.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No teams available. Tournament teams will be added after Selection Sunday.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(teamsByRegion).map(([region, regionTeams]) => (
                      <div key={region}>
                        <h3 className={`text-md font-semibold mb-3 ${REGION_COLORS[region]?.text || 'text-gray-700'}`}>
                          {region} Region
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {regionTeams.map((team) => {
                            const isSelected = isTeamSelectedForDate(team.id, selectedDate);
                            const wasUsedPreviously = usedTeams.includes(team.id) && !isSelected;
                            const isEliminated = eliminatedTeamIds.has(team.id);
                            const isDisabled = wasUsedPreviously || selectedDayLocked || (isEliminated && !isSelected);

                            return (
                              <button
                                key={team.id}
                                type="button"
                                onClick={() => toggleTeamSelection(team, selectedDate)}
                                disabled={isDisabled}
                                className={`p-3 rounded-lg border-2 text-left transition-all ${
                                  isSelected
                                    ? 'bg-green-100 border-green-500 ring-2 ring-green-300'
                                    : isEliminated
                                    ? 'bg-red-50 border-red-200 opacity-40 cursor-not-allowed line-through'
                                    : wasUsedPreviously
                                    ? 'bg-gray-100 border-gray-300 opacity-50 cursor-not-allowed'
                                    : `${REGION_COLORS[region]?.bg || 'bg-gray-50'} ${REGION_COLORS[region]?.border || 'border-gray-200'} hover:border-blue-400`
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-bold text-lg text-gray-700">#{team.seed}</span>
                                  {isSelected && <span className="text-green-600">✓</span>}
                                  {isEliminated && !isSelected && <span className="text-red-400">✗</span>}
                                  {wasUsedPreviously && !isEliminated && <span className="text-red-500">✗</span>}
                                </div>
                                <p className={`font-medium truncate ${isEliminated && !isSelected ? 'text-gray-400' : 'text-gray-900'}`}>{team.name}</p>
                                {isEliminated && !isSelected && (
                                  <p className="text-xs text-red-400">Eliminated</p>
                                )}
                                {wasUsedPreviously && !isEliminated && (
                                  <p className="text-xs text-red-500">Already used</p>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
                );
              })()}

              {/* Entry fee notice — payment happens after submit (Settle Up) */}
              {pool && pool.entryFee && Number(pool.entryFee) > 0 && !existingEntry && (
                <FeeNotice entryFee={Number(pool.entryFee)} />
              )}

              {/* Error near submit button so users see it */}
              {error && (
                <div className="mb-4 bg-red-50 border-2 border-red-300 rounded-lg p-4 text-red-800 font-medium" role="alert">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={submitting || gameDays.length === 0 || !selectedDate || currentPicks.length !== requiredPicks}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  {submitting ? 'Saving...' : existingEntry ? 'Update Picks' : 'Submit Picks'}
                </button>
                <Link
                  to={`/pools/${poolId}`}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </Link>
              </div>
            </form>
          </div>

          {/* Sidebar - Summary */}
          <div className="lg:col-span-1">
            {/* Today's Picks */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-6 sticky top-4">
              <h3 className="font-semibold text-gray-900 mb-3">
                Picks for {currentGameDay ? currentGameDay.label : formatDateDisplay(selectedDate)}
              </h3>
              <div className="space-y-2">
                {currentPicks.length === 0 ? (
                  <p className="text-gray-500 text-sm">No teams selected</p>
                ) : (
                  currentPicks.map((pick) => (
                    <div
                      key={pick.teamId}
                      className="flex items-center justify-between p-2 bg-green-50 rounded-lg border border-green-200"
                    >
                      <div>
                        <span className="font-bold text-gray-700">#{pick.seed}</span>
                        <span className="ml-2 text-gray-900">{pick.teamName}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const team = teams.find(t => t.id === pick.teamId);
                          if (team) toggleTeamSelection(team, selectedDate);
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        ✕
                      </button>
                    </div>
                  ))
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  {currentPicks.length}/{requiredPicks} picks selected
                </p>
              </div>
            </div>

            {/* Used Teams List */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="font-semibold text-gray-900 mb-3">
                Previously Used Teams ({usedTeams.length})
              </h3>
              <div className="max-h-60 overflow-y-auto space-y-1">
                {usedTeams.length === 0 ? (
                  <p className="text-gray-500 text-sm">No teams used yet</p>
                ) : (
                  usedTeams.map((teamId) => {
                    const team = teams.find(t => t.id === teamId);
                    if (!team) return null;
                    const isCurrentPick = currentPicks.some(p => p.teamId === teamId);
                    return (
                      <div
                        key={teamId}
                        className={`text-sm p-1 rounded ${
                          isCurrentPick ? 'text-green-700' : 'text-gray-500 line-through'
                        }`}
                      >
                        #{team.seed} {team.name}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
